import { useCallback, useEffect, useState } from 'react';
import {
  isApiError,
  usePaymentApi,
  usePaymentInfoByResidentIdAsFamilyMember,
} from '@pflegenavi/frontend/api-nursing-home';
import { loadStripe } from '@stripe/stripe-js';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useFMContext } from '@pflegenavi/frontend/family-member-context';

export enum MandateStatus {
  Idle,
  Loading,
  Success,
  Error,
}

interface UseSetupDirectDebitResult {
  error: string | undefined;
  mandateStatus: MandateStatus;
  confirm: (params: DirectDebitParams) => void;
}

interface DirectDebitParams {
  name: string;
  email: string;
  iban: string;
}

export const useSetupDirectDebit = (): UseSetupDirectDebitResult => {
  const api = usePaymentApi();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const { selectedResidentId } = useFMContext();

  const [mandateStatus, setMandateStatus] = useState(MandateStatus.Idle);
  const [error, setError] = useState<undefined | string>(undefined);

  useEffect(() => {
    setError(undefined);
    setMandateStatus(MandateStatus.Idle);
  }, []);

  const { refetch: refetchPaymentInfo } =
    usePaymentInfoByResidentIdAsFamilyMember(selectedResidentId ?? '');

  const confirm = useCallback(
    ({ name, email, iban }: DirectDebitParams) => {
      async function setup({ name, email, iban }: DirectDebitParams) {
        try {
          if (!selectedResidentId) {
            setMandateStatus(MandateStatus.Error);
            return;
          }
          setMandateStatus(MandateStatus.Loading);
          const result = await api.setup({
            body: undefined,
            params: { residentId: selectedResidentId },
          });

          if (isApiError(result)) {
            setMandateStatus(MandateStatus.Error);
            setError(result.message);
            return;
          }

          const stripe = await loadStripe(result.publishableKey);

          if (stripe) {
            const { error } = await stripe.confirmSepaDebitSetup(
              result.paymentIntent,
              {
                payment_method: {
                  sepa_debit: { iban },
                  billing_details: {
                    name,
                    email,
                  },
                },
              }
            );
            if (!error) {
              await api.setupConfirm({
                body: undefined,
                params: { residentId: selectedResidentId },
              });
              await refetchPaymentInfo();
              setMandateStatus(MandateStatus.Success);
              enqueueSnackbar(
                t('mobile.family-member.mandate-activation-success'),
                {
                  variant: 'success',
                }
              );
            } else {
              setMandateStatus(MandateStatus.Error);
              enqueueSnackbar(
                t('mobile.family-member.mandate-activation-failure'),
                {
                  variant: 'error',
                }
              );
            }
          }
        } catch {
          setMandateStatus(MandateStatus.Error);
        }
      }

      setup({
        name,
        email,
        iban,
      });
    },
    [api, enqueueSnackbar, refetchPaymentInfo, selectedResidentId, t]
  );

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | undefined;
    if (mandateStatus === MandateStatus.Success) {
      timeout = setTimeout(() => {
        setMandateStatus((status) => {
          if (status === MandateStatus.Success) {
            return MandateStatus.Idle;
          }
          return status;
        });
      }, 3000);
    }
    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [mandateStatus]);

  return {
    error,
    mandateStatus,
    confirm,
  };
};
